import { useAuth } from '../../../../hooks'
import React, { FC, ReactNode, useEffect } from 'react'
import { Button, Dialog, Input } from '../../../../components/ui'
import { useMfaStatus, useMfaToggle } from '../../../../hooks/api/useMfa'
import { Visibility } from '@mui/icons-material'
import { Controller, useForm } from 'react-hook-form'
import cn from '../../../../lib/utils/cn'
import { useQueryClient } from '@tanstack/react-query'
import { MfaStatusResponse } from '../../../../lib/api/mfa.services'
import toastCaller from '../../../../lib/utils/toastCaller'

interface MfaConfirmationModalProps {
	isOpen: boolean
	icon?: ReactNode | null
	onClose: () => void
	onCancel?: () => void
	title: string
	description: ReactNode | string
	actionLabel?: string
	cancelLabel?: string
	cancelIcon?: ReactNode
	actionIcon?: ReactNode
	loading?: boolean
	cancelLoading?: boolean
	className?: string
	overlayClassName?: string
	currentValue?: boolean
}

const MfaConfirmationModal: FC<MfaConfirmationModalProps> = ({
	isOpen,
	icon = null,
	onClose,
	onCancel,
	title,
	description,
	cancelLabel = 'No',
	actionLabel = 'Yes',
	cancelIcon,
	actionIcon,
	loading = false,
	cancelLoading = false,
	className,
	overlayClassName,
	currentValue,
}) => {
	const { currentUser, isAuthenticated } = useAuth()
	const [mfaToken, setMfaToken] = React.useState<string | null>(null)
	const [showPassword, setShowPassword] = React.useState(false)
	const [infoMessage, setInfoMessage] = React.useState<{
		type: 'info' | 'error'
		message: string
	} | null>(null)

	const { data: mfaData } = useMfaStatus({
		staleTime: 1000 * 60 * 5,
		enabled: Boolean(!currentUser?.isSocial && isAuthenticated && currentUser?.id),
	})

	const { mutateAsync: toggleMfaMutate, isPending: isToggleMfaPending } =
		useMfaToggle()
	const queryClient = useQueryClient()

	type MfaFormValues = {
		password: string
		otp: string
	}

	const {
		control,
		handleSubmit,
		setError,
		clearErrors,
		reset,
		resetField,
		setValue,
		setFocus,
		formState: { errors },
	} = useForm<MfaFormValues>({
		defaultValues: { password: '', otp: '' },
		mode: 'onSubmit',
	})

	useEffect(() => {
		if (mfaToken) {
			setFocus('otp')
		}
	}, [mfaToken, setFocus])

	const updateMfaStatusCache = (nextValue?: boolean) => {
		if (typeof nextValue !== 'boolean') {
			return
		}

		queryClient.setQueryData<MfaStatusResponse | undefined>(
			['mfa-status'],
			(prev) => ({
				success: prev?.success ?? true,
				data: {
					value: nextValue,
				},
			})
		)
	}

	const onPasswordSubmit = async ({ password }: MfaFormValues) => {
		const response = await toggleMfaMutate({
			username: currentUser?.profile?.email || '',
			password,
			value: !mfaData?.data?.value,
		})

		if (!response) {
			setInfoMessage({
				type: 'error',
				message:
					'Sorry, something went wrong while updating MFA. Please try again.',
			})
			clearErrors()
			return
		}

		if (response.data?.redirectUrl) {
			window.location.href = response.data.redirectUrl
			return
		}

		if (response?.success) {
			clearErrors()
			reset()
			setMfaToken(null)
			setInfoMessage(null)
			updateMfaStatusCache(response.data?.value)
			toastCaller({
				type: 'success',
				message: response.data?.value
					? 'Multi-factor authentication enabled.'
					: 'Multi-factor authentication disabled.',
				autoClose: 2000,
			})
			onClose()
			return
		}

		switch (response?.step) {
			case 'otp_required':
				if (response?.mfa_token) {
					setMfaToken(response.mfa_token)
					resetField('password')
					setValue('otp', '')
					setInfoMessage({
						type: 'info',
						message:
							response?.message ||
							'Please enter the 6-digit code from your authenticator app.',
					})
					clearErrors()
				}
				return
			case 'password_invalid':
				setError('password', {
					type: 'manual',
					message: response?.message || 'Incorrect password, please try again.',
				})
				resetField('password')
				setInfoMessage({
					type: 'error',
					message: response?.message || 'Incorrect password, please try again.',
				})
				return
			case 'password_required':
				setError('password', {
					type: 'manual',
					message: response?.message || 'Password is required to continue.',
				})
				setInfoMessage({
					type: 'info',
					message: response?.message || 'Password is required to continue.',
				})
				return
			case 'auth_error':
			case 'no_access_token':
				setInfoMessage({
					type: 'error',
					message:
						response?.message ||
						'Sorry, we could not update your MFA settings. Please try again later.',
				})
				clearErrors()
				return
			default:
				setInfoMessage({
					type: 'error',
					message:
						response?.message || 'Failed to update MFA. Please try again.',
				})
				clearErrors()
		}
	}

	const onOtpSubmit = async ({ otp }: MfaFormValues) => {
		if (!mfaToken) return

		const response = await toggleMfaMutate({
			mfa_token: mfaToken,
			otp,
			value: !mfaData?.data?.value,
		})

		if (!response) {
			setInfoMessage({
				type: 'error',
				message:
					'Sorry, something went wrong while verifying the code. Please try again.',
			})
			clearErrors()
			return
		}

		if (response?.success) {
			clearErrors()
			reset()
			setMfaToken(null)
			setInfoMessage(null)
			updateMfaStatusCache(response.data?.value)
			toastCaller({
				type: 'success',
				message: response.data?.value
					? 'Multi-factor authentication enabled.'
					: 'Multi-factor authentication disabled.',
				autoClose: 2000,
			})
			onClose()
			return
		}

		if (response?.step === 'otp_invalid') {
			setError('otp', {
				type: 'manual',
				message: response?.message || 'Incorrect OTP. Please try again.',
			})
			resetField('otp')
			setInfoMessage({
				type: 'error',
				message:
					response?.message ||
					'The code did not match. Please check your authenticator app and try again.',
			})
			return
		}

		if (response?.step === 'password_required') {
			setInfoMessage({
				type: 'info',
				message:
					response?.message ||
					'Password confirmation is required before entering OTP.',
			})
			clearErrors()
			return
		}

		setInfoMessage({
			type: 'error',
			message: response?.message || 'Failed to verify OTP. Please try again.',
		})
		clearErrors()
	}

	const submitPassword = handleSubmit(onPasswordSubmit)
	const submitOtp = handleSubmit(onOtpSubmit)

	const handleModalClose = () => {
		setMfaToken(null)
		setShowPassword(false)
		setInfoMessage(null)
		reset()
		clearErrors()
		onClose()
	}

	if (!isOpen) return null

	return (
		<Dialog
			isOpen={isOpen}
			onClose={handleModalClose}
			title={title}
			icon={icon}
			className={className}
			overlayClassName={overlayClassName}
			controllers={
				<div className="flex items-center justify-end gap-2 w-full">
					<Button
						variant="tertiary"
						label={cancelLabel}
						onClick={onCancel ?? onClose}
						disabled={!!loading}
						loading={!loading && cancelLoading}
						leftIcon={cancelIcon}
					/>
					<Button
						variant="primary"
						label={mfaToken ? 'Verify OTP' : actionLabel}
						onClick={mfaToken ? submitOtp : submitPassword}
						loading={isToggleMfaPending}
						leftIcon={actionIcon}
					/>
				</div>
			}
		>
			<p className="text-text-main dark:text-text-main-dark text-wrap text-sm mb-4">
				{description}
			</p>

			{infoMessage && (
				<p
					className={cn(
						'text-sm mb-3',
						infoMessage.type === 'info'
							? 'text-text-secondary dark:text-text-main-dark'
							: 'text-negative'
					)}
				>
					{infoMessage.message}
				</p>
			)}

			{/* Password confirmation UI */}
			{!mfaToken && (
				<>
					<Controller
						name="password"
						control={control}
						rules={{
							validate: (value) => {
								if (mfaToken) return true
								if (!value) {
									return 'Please enter your password to continue.'
								}
								return true
							},
						}}
						render={({ field }) => {
							const { value, onChange, ...rest } = field
							return (
								<div>
									<Input
										{...rest}
										value={value || ''}
										placeholder="Enter your password"
										wrapperClassName="flex items-center justify-center rounded-lg"
										inputClassName="w-full rounded-md h-10 text-sm p-2 "
										type={showPassword ? 'text' : 'password'}
										error={errors.password?.message}
										onChange={(e) => onChange(e.target.value)}
										rightIcon={
											<div
												className="cursor-pointer h-max flex items-center"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? (
													<Visibility className="fill-text-secondary dark:fill-text-main-dark" />
												) : (
													<Visibility className="fill-text-secondary opacity-50" />
												)}
											</div>
										}
										onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
											if (e.key === 'Enter') {
												submitPassword()
											}
										}}
									/>
									{!currentValue && (
										<p className="text-negative mt-3">
											If you had enabled MFA previously, you must remove the old
											MFA from your authenticator app
										</p>
									)}
								</div>
							)
						}}
					/>
				</>
			)}
			{/* OTP input UI */}
			{mfaToken && (
				<div className="flex flex-col items-center gap-2 w-full">
					<Controller
						name="otp"
						control={control}
						rules={{
							validate: (value) => {
								if (!mfaToken) return true
								if (!value) {
									return 'Please enter the 6-digit OTP to continue.'
								}
								if (!/^\d{6}$/.test(value)) {
									return 'OTP must be a 6-digit number.'
								}
								return true
							},
						}}
						render={({ field }) => {
							const { value, onChange, ...rest } = field
							return (
								<Input
									{...rest}
									value={value || ''}
									placeholder="Enter OTP (6-digit code)"
									wrapperClassName="flex items-center justify-center rounded-lg w-full"
									inputClassName="w-full rounded-md h-10 text-sm p-2 "
									type="text"
									error={errors.otp?.message}
									onChange={(e) => {
										const sanitized = e.target.value
											.replace(/\D/g, '')
											.slice(0, 6)
										onChange(sanitized)
									}}
									onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
										if (e.key === 'Enter') {
											submitOtp()
										}
									}}
								/>
							)
						}}
					/>
				</div>
			)}

			{errors.root?.message && (
				<p className="text-sm text-red-600 mt-2">{errors.root.message}</p>
			)}
		</Dialog>
	)
}

export default MfaConfirmationModal
